/**
 * Counsel-owned copy for the Harbor Divorce product (WP19).
 *
 * Every string in this file is legally sensitive and is reviewed by
 * supervising family-law counsel before changes ship. Keep all UPL-adjacent
 * copy here — do not inline disclaimer, DV-screening, or refund wording in
 * components — so counsel has a single file to review.
 *
 * Review status: DRAFT — pending supervising-counsel sign-off. The DV
 * screening wording additionally requires review by a domestic-violence
 * organization before launch.
 */

/** Document-preparation posture: shown on the interview and packet pages. */
export const DOC_PREP_DISCLAIMER =
  'Harbor provides self-help document preparation and educational information, not legal ' +
  'advice. We are not a law firm and are not a substitute for an attorney.';

/** Short educational-only line for navigator (Tier A) pages. */
export const EDUCATIONAL_DISCLAIMER =
  'Educational only and not legal advice. Court rules change; always confirm with your ' +
  "court's official site or clerk.";

/**
 * DV screening question. Framed around safety (not "abuse"), answerable
 * without naming the behavior, with an explicit no-storage promise (the
 * backend strips dv_flag before persisting; only the gate outcome survives).
 * Wording to be finalized with a domestic-violence organization's guidance.
 */
export const DV_SCREEN_QUESTION = 'Do you feel safe in your relationship?';
export const DV_SCREEN_HINT =
  'If there has been violence or threats, we\u2019ll point you to people who can genuinely ' +
  'help. This answer is never stored.';

/**
 * Refund policy summary shown at checkout and on the packet page. Marketing
 * must never promise "guaranteed court acceptance" — we are a DIY
 * document-preparation service, not a review-backed one.
 */
export const REFUND_POLICY_SUMMARY =
  'One case price covers petition through judgment, whether you start at filing or already ' +
  'filed. Full refund any time before your first packet is generated. If your state updates ' +
  'its forms, we regenerate against the new official versions for free.';

/** Copilot / support posture: what Harbor will not answer. */
export const NO_ADVICE_NOTICE =
  'Harbor can explain what a form or term means, but cannot tell you what you should do. ' +
  'For advice about your specific situation, talk to a family law attorney licensed in ' +
  'your state.';

/** Granularity of household items on FL-142. Counsel-owned. */
export const DISCLOSURE_GRANULARITY =
  'You do not list every fork. Group ordinary furnishings by room. Itemize only what is ' +
  'individually valuable, claimed as separate property, or likely to be disputed. If you ' +
  'have already divided household items, the form can say “already divided” or “usual and ' +
  'customary.”';

/** Valuation basis. Counsel-owned. */
export const DISCLOSURE_VALUATION =
  'Household items are listed at garage-sale value — what they would sell for today — not ' +
  'what they cost to replace. Bank and brokerage figures are the statement balance on the ' +
  'statement date, which the form allows you to write as the valuation date.';

/** Missed 60-day disclosure deadline. Counsel-owned. Do not overstate. */
export const MISSED_DISCLOSURE_DEADLINE =
  'The court will not finalize the case until both of you have exchanged preliminary ' +
  'disclosures and filed FL-141. Missing the 60-day mark does not end the case. Family ' +
  'Code section 2104(f) lets you extend the deadline in writing.';

/** LLC disposition shapes — decisions couples make, not advice. */
export const LLC_DISPOSITION_SHAPES =
  'Couples who co-own a business often choose one of three shapes: one keeps it and ' +
  'equalizes the other, both stay owners after the divorce, or they sell or dissolve and ' +
  'split. An agreed value is acceptable in an uncontested case.';

/** Third-party server. Counsel-owned. */
export const SERVER_INSTRUCTIONS =
  'Neither spouse can serve the other. Another adult mails the disclosure packet and tells ' +
  'you the date. That date goes on each FL-141.';

/** Form-revision / rejection. Counsel-owned. */
export const REJECTION_AND_REVISION =
  'If the clerk returns the packet, the notice lists what is missing. Harbor maps those ' +
  'reasons back to the stage that needs to reopen. Courts also reject superseded mandatory ' +
  'forms — if FL-150 is revised, Harbor regenerates against the new official version.';
